/**
 * 상품 옵션 그룹 API
 * GET .../product-option-groups?keyword=&status=&brandCode=&page=&size=&sort=
 * (프로젝트 스코프: /api/v1/projects/{projectCode}/product-option-groups)
 */
import { getUserInfo } from './auth';
import { CommonUtils } from './commonUtils';
import { getApiV1Url } from '../config/api';

export interface ProductOptionGroupListItem {
  projectCode?: string;
  brandCode: string;
  brandName?: string;
  code: string;
  name: string;
  status: string;
  /** 필수 여부 */
  required?: boolean;
  /** 단일 선택 여부 */
  singleChoice?: boolean;
  /** 서버가 문자열로 주는 경우 (예: REQUIRED_SINGLE) */
  selectionRule?: string;
  itemProductCount: number;
  mappingProductCount: number;
  createdAt: string;
}

export interface ProductOptionGroupListRequest {
  page?: number;
  size?: number;
  sort?: string;
  keyword?: string;
  status?: string;
  brandCode?: string;
}

export interface ProductOptionGroupListResponse {
  totalCount: number;
  groups: ProductOptionGroupListItem[];
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface ProductOptionGroupApiErrorItem {
  code: number;
  target: string;
  message: string;
}

export interface ProductOptionGroupDetail {
  projectCode: string;
  brandCode: string;
  code: string;
  status: string;
  name: string;
  mandatory: number;
  multi: number;
  maxSelect: number;
  minSelect: number;
  maxSumQuantity: number;
  minSumQuantity: number;
  createdBy?: string;
  createdAt?: string;
  updatedBy?: string;
  updatedAt?: string;
}

export interface ProductOptionGroupUpsertRequest {
  brandCode: string;
  code?: string;
  status: string;
  name: string;
  mandatory: number;
  multi: number;
  maxSelect: number;
  minSelect: number;
  maxSumQuantity: number;
  minSumQuantity: number;
}

export interface ProductOptionGroupUpsertResult {
  success: boolean;
  message: string;
  data?: ProductOptionGroupDetail;
  errors?: ProductOptionGroupApiErrorItem[];
}

export interface ProductOptionGroupItemReplaceRequestItem {
  productCode: string;
  defaultSelect: number;
  defaultQuantity: number;
  maxQuantity: number;
  minQuantity: number;
  sortOrder: string;
}

export interface ProductOptionGroupItemReplaceRequest {
  items: ProductOptionGroupItemReplaceRequestItem[];
}

export interface ProductOptionGroupItemReplaceResult {
  success: boolean;
  message: string;
  errors?: ProductOptionGroupApiErrorItem[];
}

export interface ProductOptionGroupItem {
  productCode: string;
  productName?: string;
  defaultSelect: number;
  defaultQuantity: number;
  maxQuantity: number;
  minQuantity: number;
  sortOrder: string;
}

function toBool(v: unknown): boolean | undefined {
  if (v === true || v === false) return v;
  if (v === 1 || v === '1' || v === 'true') return true;
  if (v === 0 || v === '0' || v === 'false') return false;
  return undefined;
}

/** API 행 → 목록 UI 모델 (필드명 차이 흡수) */
export function mapProductOptionGroupApiToListItem(raw: Record<string, unknown>): ProductOptionGroupListItem {
  const required = toBool(raw.required ?? raw.mandatory ?? raw.isRequired);
  const singleChoice = toBool(raw.singleChoice ?? raw.singleSelect ?? raw.single ?? raw.onlySingle);

  return {
    projectCode: typeof raw.projectCode === 'string' ? raw.projectCode : undefined,
    brandCode: String(raw.brandCode ?? ''),
    brandName: typeof raw.brandName === 'string' ? raw.brandName : undefined,
    code: String(raw.code ?? ''),
    name: String(raw.name ?? ''),
    status: String(raw.status ?? ''),
    required,
    singleChoice,
    selectionRule: typeof raw.selectionRule === 'string' ? raw.selectionRule : undefined,
    itemProductCount:
      Number(raw.itemProductCount ?? raw.itemCount ?? raw.optionItemCount ?? raw.optionCount ?? 0) || 0,
    mappingProductCount:
      Number(raw.mappingProductCount ?? raw.mappedProductCount ?? raw.productMappingCount ?? raw.productCount ?? 0) || 0,
    createdAt: String(raw.createdAt ?? ''),
  };
}

function extractGroupsPayload(data: Record<string, unknown>): unknown[] {
  const list =
    data.productOptionGroups ??
    data.groups ??
    data.items ??
    data.content ??
    data.list;
  if (Array.isArray(list)) return list;
  return [];
}

function getAuthHeaders(): Record<string, string> {
  const userInfo = getUserInfo();
  if (!userInfo) {
    throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
  }
  return {
    'Content-Type': 'application/json',
    'X-Account-Code': userInfo.accountCode,
    'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId,
  };
}

function extractApiErrors(payload: any): ProductOptionGroupApiErrorItem[] {
  const items = payload?.errors?.error;
  if (!Array.isArray(items)) return [];
  return items
    .map((e: any) => ({
      code: Number(e?.code ?? 0),
      target: String(e?.target ?? ''),
      message: String(e?.message ?? ''),
    }))
    .filter((e: ProductOptionGroupApiErrorItem) => Boolean(e.target || e.message));
}

function mapDetail(raw: any): ProductOptionGroupDetail {
  return {
    projectCode: String(raw?.projectCode ?? ''),
    brandCode: String(raw?.brandCode ?? ''),
    code: String(raw?.code ?? ''),
    status: String(raw?.status ?? ''),
    name: String(raw?.name ?? ''),
    mandatory: Number(raw?.mandatory ?? 0),
    multi: Number(raw?.multi ?? 0),
    maxSelect: Number(raw?.maxSelect ?? 0),
    minSelect: Number(raw?.minSelect ?? 0),
    maxSumQuantity: Number(raw?.maxSumQuantity ?? 0),
    minSumQuantity: Number(raw?.minSumQuantity ?? 0),
    createdBy: raw?.createdBy ? String(raw.createdBy) : undefined,
    createdAt: raw?.createdAt ? String(raw.createdAt) : undefined,
    updatedBy: raw?.updatedBy ? String(raw.updatedBy) : undefined,
    updatedAt: raw?.updatedAt ? String(raw.updatedAt) : undefined,
  };
}

function mapProductOptionGroupItem(raw: any): ProductOptionGroupItem {
  return {
    productCode: String(raw?.productCode ?? ''),
    productName: raw?.productName ? String(raw.productName) : undefined,
    defaultSelect: Number(raw?.defaultSelect ?? 0),
    defaultQuantity: Number(raw?.defaultQuantity ?? 0),
    maxQuantity: Number(raw?.maxQuantity ?? 0),
    minQuantity: Number(raw?.minQuantity ?? 0),
    sortOrder: String(raw?.sortOrder ?? ''),
  };
}

export async function getProductOptionGroupList(
  projectCode: string,
  params: ProductOptionGroupListRequest = {}
): Promise<ProductOptionGroupListResponse> {
  const userInfo = getUserInfo();
  if (!userInfo) {
    throw new Error('사용자 정보가 없습니다. 로그인을 다시 시도해주세요.');
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Account-Code': userInfo.accountCode,
    'X-User-Id': userInfo.userType === 'root' ? userInfo.accountCode : userInfo.userId,
  };

  const queryParams = new URLSearchParams();
  if (params.page !== undefined) queryParams.append('page', String(params.page));
  if (params.size !== undefined) queryParams.append('size', String(params.size));
  if (params.sort) queryParams.append('sort', params.sort);
  if (params.keyword) queryParams.append('keyword', params.keyword);
  if (params.status) queryParams.append('status', params.status);
  if (params.brandCode) queryParams.append('brandCode', params.brandCode);

  const qs = queryParams.toString();
  const url = `${getApiV1Url(`/projects/${projectCode}/product-option-groups`)}${qs ? `?${qs}` : ''}`;

  try {
    const response = await fetch(url, { method: 'GET', headers });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const msg = CommonUtils.getHttpErrorMessage(response.status, errorData);
      throw new Error(msg || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();

    if (result?.data && typeof result.data === 'object') {
      const d = result.data as Record<string, unknown>;
      const rawList = extractGroupsPayload(d);
      const groups = rawList.map((row) =>
        mapProductOptionGroupApiToListItem(row as Record<string, unknown>)
      );
      return {
        totalCount: Number(d.totalCount ?? 0) || 0,
        groups,
        totalPages: Number(d.totalPages ?? 0) || 0,
        currentPage: Number(d.currentPage ?? params.page ?? 0) || 0,
        pageSize: Number(d.pageSize ?? params.size ?? 20) || 20,
        hasNext: Boolean(d.hasNext),
        hasPrevious: Boolean(d.hasPrevious),
      };
    }

    return {
      totalCount: 0,
      groups: [],
      totalPages: 0,
      currentPage: 0,
      pageSize: params.size ?? 20,
      hasNext: false,
      hasPrevious: false,
    };
  } catch (error: unknown) {
    if (CommonUtils.isNetworkError(error)) {
      throw new Error(CommonUtils.getNetworkErrorMessage(error));
    }
    throw error;
  }
}

/** 상세 조회: GET /projects/{projectCode}/product-option-groups/{groupCode} */
export async function getProductOptionGroupDetail(
  projectCode: string,
  groupCode: string
): Promise<ProductOptionGroupDetail> {
  const headers = getAuthHeaders();
  const url = getApiV1Url(
    `/projects/${projectCode}/product-option-groups/${encodeURIComponent(groupCode)}`
  );

  try {
    const response = await fetch(url, { method: 'GET', headers });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(CommonUtils.getHttpErrorMessage(response.status, payload));
    }
    return mapDetail(payload?.data ?? {});
  } catch (error: unknown) {
    if (CommonUtils.isNetworkError(error)) {
      throw new Error(CommonUtils.getNetworkErrorMessage(error));
    }
    throw error;
  }
}

/** 생성: POST /projects/{projectCode}/product-option-groups */
export async function createProductOptionGroup(
  projectCode: string,
  request: ProductOptionGroupUpsertRequest
): Promise<ProductOptionGroupUpsertResult> {
  const headers = getAuthHeaders();
  const url = getApiV1Url(`/projects/${projectCode}/product-option-groups`);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      return {
        success: false,
        message: CommonUtils.getHttpErrorMessage(response.status, payload),
        errors: extractApiErrors(payload),
      };
    }
    return {
      success: true,
      message: '옵션 그룹이 생성되었습니다.',
      data: mapDetail(payload?.data ?? {}),
    };
  } catch (error: unknown) {
    if (CommonUtils.isNetworkError(error)) {
      throw new Error(CommonUtils.getNetworkErrorMessage(error));
    }
    throw error;
  }
}

/** 수정: POST /projects/{projectCode}/product-option-groups/{groupCode} */
export async function updateProductOptionGroup(
  projectCode: string,
  groupCode: string,
  request: ProductOptionGroupUpsertRequest
): Promise<ProductOptionGroupUpsertResult> {
  const headers = getAuthHeaders();
  const url = getApiV1Url(
    `/projects/${projectCode}/product-option-groups/${encodeURIComponent(groupCode)}`
  );

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      return {
        success: false,
        message: CommonUtils.getHttpErrorMessage(response.status, payload),
        errors: extractApiErrors(payload),
      };
    }
    return {
      success: true,
      message: '옵션 그룹이 수정되었습니다.',
      data: mapDetail(payload?.data ?? {}),
    };
  } catch (error: unknown) {
    if (CommonUtils.isNetworkError(error)) {
      throw new Error(CommonUtils.getNetworkErrorMessage(error));
    }
    throw error;
  }
}

/** 상품 매핑 교체: POST /product-option-groups/{groupCode}/product-option-items/replace */
export async function replaceProductOptionGroupItems(
  projectCode: string,
  groupCode: string,
  request: ProductOptionGroupItemReplaceRequest
): Promise<ProductOptionGroupItemReplaceResult> {
  const headers = getAuthHeaders();
  const url = getApiV1Url(
    `/projects/${projectCode}/product-option-groups/${encodeURIComponent(groupCode)}/product-option-items/replace`
  );

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      return {
        success: false,
        message: CommonUtils.getHttpErrorMessage(response.status, payload),
        errors: extractApiErrors(payload),
      };
    }
    return {
      success: true,
      message: '옵션 그룹 상품 매핑이 저장되었습니다.',
      errors: extractApiErrors(payload),
    };
  } catch (error: unknown) {
    if (CommonUtils.isNetworkError(error)) {
      throw new Error(CommonUtils.getNetworkErrorMessage(error));
    }
    throw error;
  }
}

/** 상품 매핑 조회: GET /projects/{projectCode}/product-option-groups/{groupCode}/product-option-items */
export async function getProductOptionGroupItems(
  projectCode: string,
  groupCode: string
): Promise<ProductOptionGroupItem[]> {
  const headers = getAuthHeaders();
  const url = getApiV1Url(
    `/projects/${projectCode}/product-option-groups/${encodeURIComponent(groupCode)}/product-option-items`
  );

  try {
    const response = await fetch(url, { method: 'GET', headers });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(CommonUtils.getHttpErrorMessage(response.status, payload));
    }

    const rows = Array.isArray(payload?.data?.items)
        ? payload.data.items
      : Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.items)
          ? payload.items
          : [];

    console.log('[옵션그룹 상품 조회 응답]', {
      projectCode,
      groupCode,
      status: response.status,
      itemCount: rows.length,
      payload,
    });

    return rows.map((row: any) => mapProductOptionGroupItem(row));
  } catch (error: unknown) {
    if (CommonUtils.isNetworkError(error)) {
      throw new Error(CommonUtils.getNetworkErrorMessage(error));
    }
    throw error;
  }
}

/** 상품 기준: 매핑된 옵션 그룹 한 건 (GET 응답) */
export interface ProductToGroupMappingItem {
  groupCode: string;
  groupName?: string;
  defaultSelect: number;
  defaultQuantity: number;
  maxQuantity: number;
  minQuantity: number;
  sortOrder: string;
  /** 목록과 동일한 표시용 필드 (있으면) */
  status?: string;
  itemProductCount?: number;
  mappingProductCount?: number;
}

export interface ProductToGroupMappingReplaceRequest {
  items: Array<{
    groupCode: string;
    defaultSelect: number;
    defaultQuantity: number;
    maxQuantity: number;
    minQuantity: number;
    sortOrder: string;
  }>;
}

function mapProductToGroupMappingItem(raw: any): ProductToGroupMappingItem {
  return {
    groupCode: String(raw?.groupCode ?? raw?.code ?? ''),
    groupName: raw?.groupName ? String(raw.groupName) : raw?.name ? String(raw.name) : undefined,
    defaultSelect: Number(raw?.defaultSelect ?? 0),
    defaultQuantity: Number(raw?.defaultQuantity ?? 0),
    maxQuantity: Number(raw?.maxQuantity ?? 0),
    minQuantity: Number(raw?.minQuantity ?? 0),
    sortOrder: String(raw?.sortOrder ?? ''),
    status: raw?.status != null && raw?.status !== '' ? String(raw.status) : undefined,
    itemProductCount:
      raw?.itemProductCount != null || raw?.itemCount != null
        ? Number(raw?.itemProductCount ?? raw?.itemCount ?? 0)
        : undefined,
    mappingProductCount:
      raw?.mappingProductCount != null || raw?.mappedProductCount != null
        ? Number(raw?.mappingProductCount ?? raw?.mappedProductCount ?? 0)
        : undefined,
  };
}

/**
 * 상품에 매핑된 옵션 그룹 목록
 * GET /projects/{projectCode}/products/{productCode}/product-option-mappings
 */
export async function getProductToGroupMappings(
  projectCode: string,
  productCode: string
): Promise<ProductToGroupMappingItem[]> {
  const headers = getAuthHeaders();
  const url = getApiV1Url(
    `/projects/${projectCode}/products/${encodeURIComponent(productCode)}/product-option-mappings`
  );

  try {
    const response = await fetch(url, { method: 'GET', headers });
    const payload = await response.json().catch(() => ({}));
    if (response.status === 404) {
      return [];
    }
    if (!response.ok) {
      throw new Error(CommonUtils.getHttpErrorMessage(response.status, payload));
    }

    const rows = Array.isArray(payload?.data?.productOptionMappings)
      ? payload.data.productOptionMappings
      : Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.productOptionMappings)
          ? payload.productOptionMappings
          : [];

    return rows.map((row: any) => mapProductToGroupMappingItem(row));
  } catch (error: unknown) {
    if (CommonUtils.isNetworkError(error)) {
      throw new Error(CommonUtils.getNetworkErrorMessage(error));
    }
    throw error;
  }
}

/**
 * 상품–옵션그룹 매핑 교체
 * POST /projects/{projectCode}/products/{productCode}/product-option-mappings/replace
 */
export async function replaceProductToGroupMappings(
  projectCode: string,
  productCode: string,
  request: ProductToGroupMappingReplaceRequest
): Promise<ProductOptionGroupItemReplaceResult> {
  const headers = getAuthHeaders();
  const url = getApiV1Url(
    `/projects/${projectCode}/products/${encodeURIComponent(productCode)}/product-option-mappings/replace`
  );

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
      return {
        success: false,
        message: CommonUtils.getHttpErrorMessage(response.status, payload),
        errors: extractApiErrors(payload),
      };
    }
    return {
      success: true,
      message: '상품 옵션 그룹 매핑이 저장되었습니다.',
      errors: extractApiErrors(payload),
    };
  } catch (error: unknown) {
    if (CommonUtils.isNetworkError(error)) {
      throw new Error(CommonUtils.getNetworkErrorMessage(error));
    }
    throw error;
  }
}